import { useState } from "react";
import {
  FileText,
  FileSpreadsheet,
  BarChart3,
  Calendar,
  MapPin,
  Users,
  Lightbulb,
  BookOpen,
  Award,
  TrendingUp,
  AlertTriangle,
  Settings,
  GraduationCap,
  ChevronRight,
  ChevronDown,
} from "lucide-react";

import {
  Card,
  PageHeader,
  Badge,
  useToast,
  formatDate,
} from "../adminComponents/adminComponents";
import { useAuth } from "../../context/authContext";
import { ROLES } from "../../constants/roles";
import { useAdminReportsHook } from "../../hooks/useAdminHooks";
import { useManagerReportsHook } from "../../hooks/useManagerHooks";
import "./reports.css";

const DEFAULT_REPORTS_DATA = {
  time_periods: [
    { value: "q4-2025", label: "Q4 2025" },
    { value: "q3-2025", label: "Q3 2025" },
  ],
  cohorts: [{ value: "2026-Q1", label: "2026-Q1" }],
  sectors: [
    { value: "all", label: "All Sectors" },
    { value: "agriculture", label: "Agriculture" },
    { value: "technology", label: "Technology" },
    { value: "health", label: "Health" },
    { value: "energy", label: "Energy" },
    { value: "education", label: "Education" },
    { value: "manufacturing", label: "Manufacturing" },
    { value: "services", label: "Services" },
  ],
  regions: [
    { value: "Kampala", label: "Kampala" },
    { value: "Gulu", label: "Gulu" },
  ],
  standard_reports: [
    {
      id: "",
      name: "Weekly Snapshot",
      description: "Quick portfolio performance summary",
      formats: [
        {
          format_type: "PDF",
          link: "https://example.com/reports/weekly-snapshot.pdf",
        },
      ],
      last_generated: "2026-05-17",
      icon: "calendar",
    },
    {
      id: "",
      name: "Monthly Performance",
      description: "Detailed metrics and venture-level analysis",
      formats: [
        {
          format_type: "PDF",
          link: "https://example.com/reports/monthly-performance.pdf",
        },
        {
          format_type: "Excel",
          link: "https://example.com/reports/monthly-performance.xlsx",
        },
      ],
      last_generated: "2026-05-11",
      icon: "bar-chart-3",
    },
    {
      id: "",
      name: "Quarterly Impact",
      description: "Aggregated outcomes and impact metrics",
      formats: [
        {
          format_type: "PDF",
          link: "https://example.com/reports/quarterly-impact.pdf",
        },
        {
          format_type: "Excel",
          link: "https://example.com/reports/quarterly-impact.xlsx",
        },
      ],
      last_generated: "2026-04-04",
      icon: "trending-up",
    },
    {
      id: "",
      name: "Annual Outcomes",
      description: "Comprehensive year-end report with all indicators",
      formats: [
        {
          format_type: "PDF",
          link: "https://example.com/reports/annual-outcomes.pdf",
        },
        {
          format_type: "Excel",
          link: "https://example.com/reports/annual-outcomes.xlsx",
        },
      ],
      last_generated: "2025-11-20",
      icon: "award",
    },
  ],
  learning_and_insights: {
    trends: {
      label: "Key Trends Identified",
      items: [
        "Women-led ventures show 23% higher 6-month survival rate",
        "Agriculture sector ventures have strongest community impact",
        "Level 3+ ventures demonstrate most consistent revenue growth",
        "Early-stage coaching correlates with better repayment outcomes",
      ],
    },
    working: {
      label: "What's Working",
      items: [
        "Peer learning circles improve business skills adoption",
        "Monthly revenue check-ins reduce data lag",
        "Cohort-based progression creates healthy competition",
        "Linked capital + coaching model outperforms capital-only",
      ],
    },
    not_working: {
      label: "What's Not Working",
      items: [
        "Long survey forms reduce completion rates",
        "Quarterly check-ins are too infrequent for early ventures",
        "Tech-based ventures need specialized mentorship",
        "Remote ventures harder to reach for data collection",
      ],
    },
    adjustments: {
      label: "Program Adjustments Logged",
      items: [
        {
          date: "2025-Q4",
          change: "Introduced mobile-first data collection",
          impact: "30% increase in submission rates",
        },
      ],
    },
    cohort_lessons: {
      label: "Lessons Learned by Cohort",
      items: [
        {
          cohort: "2024",
          lesson:
            "Strong cohort with high engagement. Focus on scaling successful ventures.",
        },
      ],
    },
  },
  success_stories: [
    {
      venture: "Green Harvest Foods",
      region: "Kampala",
      highlight: "Grew from UGX 1.2M to UGX 12.5M monthly revenue in 18 months",
      jobs: 10,
      beneficiaries: 2450,
      verified: true,
    },
    {
      venture: "Kasubi Crafts",
      region: "Jinja",
      highlight: "Created 12 jobs for women in rural community",
      jobs: 12,
      beneficiaries: 980,
      verified: true,
    },
  ],
  reflection_notes: {
    notes: "",
    added_at: null,
    added_by: null,
  },
};

const REPORT_ICONS = {
  calendar: Calendar,
  "bar-chart-3": BarChart3,
  "trending-up": TrendingUp,
  award: Award,
};

const FORMAT_ICONS = {
  PDF: FileText,
  Excel: FileSpreadsheet,
};

const ACCORDION = [
  {
    id: "trends",
    icon: TrendingUp,
    tone: "primary",
  },
  { id: "working", icon: Award, tone: "success" },
  {
    id: "not_working",
    icon: AlertTriangle,
    tone: "warning",
  },
  {
    id: "adjustments",
    icon: Settings,
    tone: "muted",
  },
  {
    id: "cohort_lessons",
    icon: GraduationCap,
    tone: "accent",
  },
];

export default function Reports() {
  const { user, token } = useAuth();
  const { toast, ToastViewport } = useToast();
  const [filters, setFilters] = useState({
    period: "",
    cohort: "",
    sector: "",
    region: "",
  });
  const [reflectionDraft, setReflectionDraft] = useState(null);
  const [openSections, setOpenSections] = useState(["trends"]);

  const isAdmin = user?.role === ROLES.ADMIN;
  const isManager = user?.role === ROLES.MANAGER;

  const adminReportsQuery = useAdminReportsHook({
    token,
    userId: user?.id,
    enabled: isAdmin,
  });

  const managerReportsQuery = useManagerReportsHook({
    token,
    userId: user?.id,
    enabled: isManager,
  });

  const reportsApiData = isManager
    ? managerReportsQuery.data
    : adminReportsQuery.data;
  const isLoading = isManager
    ? managerReportsQuery.isLoading
    : adminReportsQuery.isLoading;

  const reportsData = reportsApiData ?? DEFAULT_REPORTS_DATA;
  const insights = reportsData?.learning_and_insights ?? {};
  const selectedFilters = {
    period: filters.period || reportsData?.time_periods?.[0]?.value || "",
    cohort: filters.cohort || reportsData?.cohorts?.[0]?.value || "",
    sector: filters.sector || reportsData?.sectors?.[0]?.value || "",
    region: filters.region || reportsData?.regions?.[0]?.value || "",
  };
  const reflectionValue =
    reflectionDraft ?? reportsData?.reflection_notes?.notes ?? "";

  const setFilter = (key, value) =>
    setFilters((current) => ({ ...current, [key]: value }));
  const toggleSection = (id) =>
    setOpenSections((current) =>
      current.includes(id)
        ? current.filter((sectionId) => sectionId !== id)
        : [...current, id],
    );

  const handleDownload = (report, format) => {
    if (format?.link) {
      window.open(format.link, "_blank", "noopener,noreferrer");
    }

    toast({
      title: "Download started",
      description: `Opening ${report} (${format?.format_type ?? "file"})…`,
    });
  };

  const handleExport = (kind) =>
    toast({
      title: "Export initiated",
      description: `Preparing ${kind} export for ${selectedFilters.period || "selected period"}…`,
    });

  const handleSaveNotes = () =>
    toast({
      title: "Notes saved",
      description: "Your reflection notes have been saved.",
    });

  if (isLoading && (isAdmin || isManager)) {
    return (
      <div className="adminReports-root">
        <PageHeader
          title="Reports & Learning"
          description="Turn monitoring data into insights, learning, and communication assets"
        />
        <p className="admin-muted">Loading reports data...</p>
      </div>
    );
  }

  return (
    <div className="adminReports-root">
      <PageHeader
        title="Reports & Learning"
        description="Turn monitoring data into insights, learning, and communication assets"
      />

      {/* Filters */}
      <Card
        title={
          <span className="adminReports-titleRow">
            <Settings className="adminReports-titleIcon" /> Report Filters
          </span>
        }
      >
        <div className="admin-grid admin-grid-4 adminReports-filters">
          <Field label="Time Period">
            <select
              className="admin-select"
              value={selectedFilters.period}
              onChange={(event) => setFilter("period", event.target.value)}
            >
              {reportsData?.time_periods?.map((period) => (
                <option key={period.value} value={period.value}>
                  {period.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Cohort">
            <select
              className="admin-select"
              value={selectedFilters.cohort}
              onChange={(event) => setFilter("cohort", event.target.value)}
            >
              {reportsData?.cohorts?.map((cohort) => (
                <option key={cohort.value} value={cohort.value}>
                  {cohort.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Sector">
            <select
              className="admin-select"
              value={selectedFilters.sector}
              onChange={(event) => setFilter("sector", event.target.value)}
            >
              {reportsData?.sectors?.map((sector) => (
                <option key={sector.value} value={sector.value}>
                  {sector.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Region">
            <select
              className="admin-select"
              value={selectedFilters.region}
              onChange={(event) => setFilter("region", event.target.value)}
            >
              {reportsData?.regions?.map((region) => (
                <option key={region.value} value={region.value}>
                  {region.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </Card>

      {/* Standard Reports */}
      <section>
        <h2 className="adminReports-sectionTitle">Standard Reports</h2>
        <div className="admin-grid admin-grid-4">
          {reportsData?.standard_reports?.map((report, index) => {
            const ReportIcon = REPORT_ICONS[report.icon] ?? FileText;
            return (
              <Card key={`${report.name}-${index}`}>
                <div className="adminReports-reportHead">
                  <ReportIcon className="adminReports-reportIcon" />
                  <div>
                    <p className="adminReports-reportName">{report.name}</p>
                    <p className="admin-muted adminReports-reportDesc">
                      {report.description}
                    </p>
                  </div>
                </div>
                <p className="admin-muted adminReports-reportMeta">
                  Last generated: {formatDate(report.last_generated)}
                </p>
                <div className="admin-row">
                  {report.formats.map((format) => {
                    const FormatIcon =
                      FORMAT_ICONS[format.format_type] ?? BarChart3;
                    return (
                      <button
                        key={`${report.name}-${format.format_type}`}
                        type="button"
                        className="admin-btn admin-btn-outline admin-btn-sm"
                        onClick={() => handleDownload(report.name, format)}
                      >
                        <FormatIcon className="admin-btn-icon" />
                        {format.format_type}
                      </button>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Learning & Insights accordion */}
      <Card
        title={
          <span className="adminReports-titleRow">
            <Lightbulb className="adminReports-titleIcon adminReports-iconLight" />
            Learning & Insights
          </span>
        }
        description="Key patterns and learnings from portfolio data"
      >
        <div className="adminReports-accordion">
          {ACCORDION.map((section) => {
            const open = openSections.includes(section.id);
            const Icon = section.icon;
            const sectionData = insights?.[section.id] ?? {};
            return (
              <div key={section.id} className="adminReports-accItem">
                <button
                  type="button"
                  className="adminReports-accTrigger"
                  aria-expanded={open}
                  onClick={() => toggleSection(section.id)}
                >
                  <span className="adminReports-accLabel">
                    <Icon
                      className={`adminReports-accIcon adminReports-tone-${section.tone}`}
                    />
                    {sectionData.label ?? section.id}
                  </span>
                  <ChevronDown
                    className={
                      "adminReports-accChevron " +
                      (open ? "adminReports-accChevron-open" : "")
                    }
                  />
                </button>
                {open && (
                  <div className="adminReports-accBody">
                    <SectionContent id={section.id} insights={insights} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Knowledge assets */}
      <div className="admin-grid admin-grid-2">
        <Card
          title={
            <span className="adminReports-titleRow">
              <Award className="adminReports-titleIcon adminReports-iconPrimary" />
              Verified Success Stories
            </span>
          }
          description="Curated stories linked to real metrics"
        >
          <div className="adminReports-stories">
            {reportsData?.success_stories?.map((story, index) => (
              <div key={index} className="adminReports-story">
                <div className="adminReports-storyHead">
                  <div>
                    <p className="adminReports-storyName">{story.venture}</p>
                    <p className="admin-muted adminReports-storyMeta">
                      <MapPin className="adminReports-storyIcon" />{" "}
                      {story.region}
                    </p>
                  </div>
                  {story.verified && (
                    <Badge tone="primary">
                      <Award className="adminReports-storyIcon" /> Verified
                    </Badge>
                  )}
                </div>
                <p className="adminReports-storyHighlight">{story.highlight}</p>
                <div className="admin-row adminReports-storyStats admin-muted">
                  <span>
                    <Users className="adminReports-storyIcon" /> {story.jobs}{" "}
                    jobs
                  </span>
                  <span>{story.beneficiaries} end customers</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card
          title={
            <span className="adminReports-titleRow">
              <BookOpen className="adminReports-titleIcon" />
              Reflection Notes
            </span>
          }
          description="Internal notes (not included in reports)"
        >
          <textarea
            className="admin-textarea adminReports-textarea"
            placeholder="Add your reflection notes, observations, or ideas for program improvement..."
            value={reflectionValue}
            onChange={(event) => setReflectionDraft(event.target.value)}
          />
          {reportsData?.reflection_notes?.added_at && (
            <p className="admin-muted adminReports-reportMeta">
              Last added {formatDate(reportsData.reflection_notes.added_at)} by{" "}
              {reportsData.reflection_notes.added_by}
            </p>
          )}
          <button
            type="button"
            className="admin-btn admin-btn-primary"
            onClick={handleSaveNotes}
            disabled={!reflectionValue.trim()}
          >
            Save Notes
          </button>
        </Card>
      </div>

      {/* Export actions */}
      <Card
        title="Export Options"
        description="Generate exports with current filter settings"
      >
        <div className="admin-row">
          <button
            type="button"
            className="admin-btn admin-btn-primary"
            onClick={() => handleExport("PDF Report")}
          >
            <FileText className="admin-btn-icon" /> Generate PDF Report
          </button>
          <button
            type="button"
            className="admin-btn admin-btn-outline"
            onClick={() => handleExport("Excel Data")}
          >
            <FileSpreadsheet className="admin-btn-icon" /> Export Raw Data
            (Excel)
          </button>
          <button
            type="button"
            className="admin-btn admin-btn-outline"
            onClick={() => handleExport("Charts Package")}
          >
            <BarChart3 className="admin-btn-icon" /> Download Charts Package
          </button>
          <button
            type="button"
            className="admin-btn admin-btn-outline"
            onClick={() => handleExport("Learning Summary")}
          >
            <Lightbulb className="admin-btn-icon" /> Export Learning Summary
          </button>
        </div>
      </Card>

      <ToastViewport />
    </div>
  );
}

// ---------- helpers ----------

function Field({ label, children }) {
  return (
    <label className="adminReports-field">
      <span className="admin-label">{label}</span>
      {children}
    </label>
  );
}

function BulletList({ items, tone }) {
  if (!items?.length) {
    return <p className="admin-muted">No insights available.</p>;
  }

  return (
    <ul className="adminReports-bullets">
      {items.map((item, index) => (
        <li key={index}>
          <ChevronRight
            className={`adminReports-bulletIcon adminReports-tone-${tone}`}
          />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function SectionContent({ id, insights }) {
  const section = insights?.[id] ?? {};

  switch (id) {
    case "trends":
      return <BulletList items={section.items ?? []} tone="primary" />;
    case "working":
      return <BulletList items={section.items ?? []} tone="success" />;
    case "not_working":
      return <BulletList items={section.items ?? []} tone="warning" />;
    case "adjustments":
      return (
        <div className="adminReports-adjustments">
          {(section.items ?? []).map((adjustment, index) => (
            <div key={index} className="adminReports-adjustment">
              <Badge tone="outline">{adjustment.date}</Badge>
              <div>
                <p className="adminReports-adjustChange">{adjustment.change}</p>
                <p className="admin-muted adminReports-adjustImpact">
                  Impact: {adjustment.impact}
                </p>
              </div>
            </div>
          ))}
        </div>
      );
    case "cohort_lessons":
      return (
        <div className="adminReports-cohorts">
          {(section.items ?? []).map((item) => (
            <div key={item.cohort} className="adminReports-cohort">
              <Badge tone="primary">{item.cohort}</Badge>
              <p className="adminReports-cohortText">{item.lesson}</p>
            </div>
          ))}
        </div>
      );
    default:
      return null;
  }
}
